import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import PageHeader from "@/shared/components/layout/dashboard-layout/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/ui/components/ui/card";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { Label } from "@/ui/components/ui/label";
import { useInviteUser } from "@/users/api/queries/user.queries";

const UserCreate = () => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const { mutateAsync: inviteUser, isPending } = useInviteUser();

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    username: "",
    email: "",
    organizations: [],
  });

  const breadcrumbs = [
    { path: "/settings/user", breadcrumb: t("breadcrumbs.users") },
    { path: "/settings/user/add", breadcrumb: t("breadcrumbs.addUser") },
  ];

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInvite = async () => {
    await inviteUser(form);
    navigate("/settings/user");
  };

  return (
    <div className="h-full">
      <PageHeader breadcrumbs={breadcrumbs} />

      <div className="p-6 space-y-6 max-w-4xl">
        <div>
          <h1 className="text-2xl font-bold">{t("userCreate.title")}</h1>
          <p className="text-muted-foreground">
            {t("userCreate.description")}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t("userCreate.infoTitle")}</CardTitle>
            <CardDescription>
              {t("userCreate.infoDescription")}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t("userCreate.firstNameLabel")}</Label>
                <Input
                  id="first_name"
                  placeholder={t("userCreate.firstNamePlaceholder")}
                  value={form.first_name}
                  onChange={(e) =>
                    handleChange("first_name", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">{t("userCreate.lastNameLabel")}</Label>
                <Input
                  id="last_name"
                  placeholder={t("userCreate.lastNamePlaceholder")}
                  value={form.last_name}
                  onChange={(e) =>
                    handleChange("last_name", e.target.value)
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">{t("userCreate.usernameLabel")}</Label>
              <Input
                id="username"
                placeholder={t("userCreate.usernamePlaceholder")}
                value={form.username}
                onChange={(e) =>
                  handleChange("username", e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("userCreate.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("userCreate.emailPlaceholder")}
                value={form.email}
                onChange={(e) =>
                  handleChange("email", e.target.value)
                }
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/settings/user")}
          >
            {t("actions.cancel")}
          </Button>

          <Button onClick={handleInvite} disabled={isPending}>
            {isPending ? t("userCreate.submitting") : t("userCreate.submit")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default UserCreate;